import * as graph from '@adc/sdk'
import type {
  Listing,
  ListingStatus,
  OccupancyStatus,
  Maybe,
} from '@adc/sdk/types'

const DEFAULT_FAQ_BID = {
  question: 'How do I place a bid?',
  shortName: 'bid',
}
const DEFAULT_FAQ_PAY = {
  question: 'If I win, when do I pay?',
  shortName: 'pay',
}
const DEFAULT_FAQ_EVICTION = {
  question: 'Will I be responsible for an eviction?',
  shortName: 'eviction',
}

const getLiveFAQs = (
  listingStatus: ListingStatus,
  occupancyStatus?: Maybe<OccupancyStatus>
) => {
  // LIVE/foreclosure FAQs
  const faqs = []

  // Universal questions (always included)
  faqs.push({
    question: 'How much money should I bring to auction?',
    shortName: 'money',
  })
  faqs.push({ question: 'Can I use a loan?', shortName: 'loan' })

  // Conditional questions
  if (listingStatus === graph.enums.ListingStatus.SalePending) {
    faqs.push({
      question: 'When will it clear for auction?',
      shortName: 'when_clear',
    })
  }

  if (listingStatus === graph.enums.ListingStatus.SalePendingCleared) {
    faqs.push({
      question: 'What does "cleared for auction" mean?',
      shortName: 'what_clear',
    })
  }

  if (occupancyStatus === 'OCCUPIED') {
    faqs.push(DEFAULT_FAQ_EVICTION)
  }

  return faqs
}

const getOnlineReoFAQs = (
  listingStatus: ListingStatus,
  occupancyStatus?: Maybe<OccupancyStatus>
) => {
  // ONLINE/REO FAQs
  const faqs = []

  // Universal questions (always included)
  faqs.push(DEFAULT_FAQ_BID)
  faqs.push({
    question: 'Can I bid on behalf of a client?',
    shortName: 'client',
  })
  faqs.push(DEFAULT_FAQ_PAY)

  // Conditional questions
  if (occupancyStatus === 'OCCUPIED') {
    faqs.push(DEFAULT_FAQ_EVICTION)
  }

  if (listingStatus === graph.enums.ListingStatus.AuctionInProgress) {
    faqs.push({
      question: 'What happens if the reserve is not met?',
      shortName: 'reserve',
    })
  }

  return faqs
}

const getThirdPartyOnlineFAQs = (occupancyStatus?: Maybe<OccupancyStatus>) => {
  // 3rd party online FAQs
  const faqs = []
  faqs.push(DEFAULT_FAQ_BID)
  faqs.push(DEFAULT_FAQ_PAY)
  if (occupancyStatus === 'OCCUPIED') {
    faqs.push(DEFAULT_FAQ_EVICTION)
  }
  return faqs
}

/**
 * Determines whether the auction has completed based on listing_status_group
 * and listing_status. Uses stable status fields rather than display labels.
 *
 * Covers:
 * - FCL "Completed - Reverted to Beneficiary" → listing_status NO_SALE
 * - FCL "Completed - Sold to 3rd Party" → listing_status_group SOLD
 * - REO "Pending" → listing_status_group PENDING
 * - REO "Closed" → listing_status CLOSED (also in SOLD group)
 */
export const isAuctionCompleted = (listing: Listing): boolean => {
  const statusGroup = listing.listing_status_group

  return (
    statusGroup === graph.enums.ListingStatusGroup.Sold ||
    statusGroup === graph.enums.ListingStatusGroup.Pending
  )
}

/**
 * Generate FAQs based on listing properties
 * @param {import('../../context/listing').CombinedListingType} listing
 * @returns {string[]} Array of FAQ questions
 */
export const getFAQS = (
  listing: Listing
): { question?: string; shortName: string }[] => {
  if (!listing) return []

  // If auction has completed, show only the post-auction FAQ
  if (isAuctionCompleted(listing)) {
    return [
      {
        question: 'Did this property sell at auction?',
        shortName: 'sold_at_auction',
      },
    ]
  }

  // Determine if this is REO ONLINE, LIVE, or CONCIERGE
  const isOnlineReo =
    listing.listing_configuration?.product_type ===
      graph.enums.ProductType.Day_1Reo ||
    listing.listing_configuration?.product_type === graph.enums.ProductType.Reo

  const isLive =
    listing.listing_configuration?.product_type ===
      graph.enums.ProductType.Trustee &&
    listing.listing_configuration.trustee_sales_channel ===
      graph.enums.CriedBy.Auctioneering &&
    listing.event?.event_type === graph.enums.EventType.Live

  const isConcierge =
    listing.listing_configuration?.product_type ===
      graph.enums.ProductType.Trustee &&
    listing.listing_configuration.trustee_sales_channel != null &&
    listing.listing_configuration.trustee_sales_channel !==
      graph.enums.CriedBy.Auctioneering

  const listingStatus = listing.listing_status
  const occupancyStatus = listing.listing_configuration?.occupancy_status
  const isThirdPartyOnline =
    listing.listing_configuration?.is_third_party_online

  if (isConcierge) {
    // CONCIERGE FAQs — same as Live for Michigan pilot launch
    if (listing.seller_property?.country_primary_subdivision === 'MI') {
      return getLiveFAQs(listingStatus, occupancyStatus)
    }
    if (isThirdPartyOnline) {
      return getThirdPartyOnlineFAQs(occupancyStatus)
    }
    return getLiveFAQs(listingStatus, occupancyStatus)
  }

  if (isLive) {
    // LIVE/foreclosure FAQs
    return getLiveFAQs(listingStatus, occupancyStatus)
  }

  if (isOnlineReo) {
    return getOnlineReoFAQs(listingStatus, occupancyStatus)
  }

  // If neither LIVE nor ONLINE, return empty array
  return []
}
